<template>
    <div>
        <b-card
            class="question mb-2"
            no-body
        >
            <template #header>
                <question-print-view-warning v-if="question && question.print_view" />
                <div class="question-header-content">
                    <CardID :id="question.id" type="Question" />
                    <div class="d-flex align-items-center">
                        <div class="d-flex flex-grow-1 align-items-center form-inline">
                            <div class="mr-2">
                                <b-form-select
                                    size="sm"
                                    v-model="question.order_number"
                                    @change="reorderQuestions"
                                    :disabled="disabled"
                                >
                                    <b-form-select-option v-for="(question, index) in questions" :key="index" :value="question.order_number">
                                        Question {{question.order_number}}
                                    </b-form-select-option>
                                </b-form-select>
                            </div>

                            <div class="ml-auto">
                                <template v-if="question.used_by_multiple_places">
                                    <b-dropdown
                                        v-if="can_duplicate_question"
                                        class="mr-2"
                                        variant="warning"
                                        size="sm"
                                    >
                                        <template #button-content>
                                            <strong>Linked Question</strong>
                                        </template>
                                        <b-dropdown-item
                                            v-if="can_duplicate_question"
                                            @click="convertToDuplicate"
                                            :disabled="!can_duplicate_question || disabled"
                                        >
                                            <font-awesome-icon :icon="['fad', 'unlink']" class="text-warning" fixed-width />
                                            Convert to Duplicate
                                        </b-dropdown-item>
                                    </b-dropdown>
                                    <b-badge
                                        v-else
                                        class="mr-2"
                                        variant="warning"
                                        size="sm"
                                    >
                                        <strong>Linked Question</strong>
                                    </b-badge>
                                </template>
                                <b-dropdown
                                    size="sm"
                                    variant="secondary"
                                    dropleft
                                >
                                    <template #button-content>
                                        <font-awesome-icon :icon="['fad', 'cog']" />
                                    </template>
                                    <b-dropdown-item
                                        @click.prevent="editQuestion(question)"
                                        :disabled="!can_update_question || disabled"
                                    >
                                        <font-awesome-icon :icon="['fad', 'edit']" class="text-primary" fixed-width /> Edit
                                    </b-dropdown-item>
                                    <b-dropdown-item
                                        @click.prevent="unlinkQuestion(question)"
                                        :disabled="!can_unlink_question || disabled"
                                    >
                                        <font-awesome-icon :icon="['fad', 'unlink']" class="text-warning" fixed-width /> Unlink
                                    </b-dropdown-item>
                                </b-dropdown>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <question-version-switcher :question="question">
                <question-content :question="question"/>
            </question-version-switcher>

            <b-card-footer
                class="bg-white"
            >
                <h6>Question Standards</h6>

                <standards-multiselect
                    v-model="question.standards"
                    :standards="available_standards"
                    :state_grade="state_grade"
                    @addStandard="addStandard"
                    @removeStandard="removeStandard"
                />
            </b-card-footer>
        </b-card>

        <question-modal
            v-model="show_question_modal"
            :unit_group="unit_group"
            :unit="unit"
            :question="question"
            :questions="questions"
            :state_grade="state_grade"
            :available_standards="available_standards"
            @update="saveQuestion"
            @hide="show_question_modal = false"
        />

    </div>
</template>

<script>
import { mapState } from 'vuex'
import CardID from '@/components/CardID'
import QuestionContent from '@/components/questions/Index'
import QuestionPrintViewWarning from '@/components/questions/QuestionPrintViewWarning'
import QuestionVersionSwitcher from '@/components/questions/QuestionVersionSwitcher'
import QuestionModal from '@/components/modals/questions/QuestionModal'
import StandardsMultiselect from '@/components/standards/StandardsMultiselect'
import {formattedQuestionType} from '@/common/formattedQuestionType'
import moment from 'moment'

export default {
    data: () => ({
        question_cache: {},
        show_question_modal: false,
    }),
    props: [
        'publication_id',
        'unit_group',
        'unit',
        'question',
        'questions',
        'disabled',
        'state_grade',
        'available_standards',
    ],
    components: {
        CardID,
        QuestionContent,
        QuestionModal,
        QuestionPrintViewWarning,
        StandardsMultiselect,
        QuestionVersionSwitcher
    },
    computed: {
        ...mapState({
            standards: state => state.standards.standards,
            user: state => state.user.user
        }),
        can_update_question(){
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'update_question');
        },
        can_unlink_question(){
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'unlink_question');
        },
        can_duplicate_question() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'duplicate_question')
        },
    },
    methods: {
        formattedQuestionType,
        editQuestion () {
            this.show_question_modal = true
        },
        unlinkQuestion (question) {
            this.$swal({
                html: 'Are you sure you want to unlink this question?',
                showCancelButton: true,
                confirmButtonText: 'Unlink Question',
                cancelButtonText: 'Cancel',
                confirmButtonColor: '#E67E22',
                reverseButtons: true
            }).then((result) => {
                if (result.value) {
                    this.axios.post('/api/units/remove_question/' + this.unit.id, {
                        question_id: question.id,
                        old_order_number: question.order_number,
                        type: question.type
                    })
                    .then(() => {
                        this.$emit('update')
                    })
                }
            })
        },
        saveQuestion (question) {
            this.axios.post('/api/questions/update/' + question.id, {
                content: question.content,
                print_view: question.print_view,
                standards: question.standards,
                available_standards: this.available_standards,
                type: question.type,
                unit_id: this.unit.id,
                questions: question.questions ? question.questions : []
            })
            .then(() => {
                this.$emit('update')

                this.show_question_modal = false
            })
        },
        reorderQuestions (event) {
            if (event) {
                this.axios.post('/api/units/reorder_question', {
                    unit_id: this.unit.id,
                    question_id: this.question.id,
                    old_order_number: this.question_cache.order_number,
                    new_order_number: event
                })
                .then(() => {
                    this.$emit('update')
                })
            }
        },
        addStandard (standard) {
            this.axios.post('/api/questions/add_standard/' + this.question.id, {
                standard_id: standard.guid
            })
            .then(() => {
                this.$emit('update')
            })
        },
        removeStandard (standard) {
            this.axios.post('/api/questions/remove_standard/' + this.question.id, {
                standard_id: standard.guid
            })
            .then(() => {
                this.$emit('update')
            })
        },
        async convertToDuplicate() {
            try {
                const message = `
                    Are you sure you want to convert this linked question to a duplicate?
                    <div class="alert alert-danger mt-2 mx-1 px-1">
                        <p>This will change the Question ID.</p>
                        <strong>Student progress may be lost mid-year!</strong>
                    </div>
                `
                const result = await this.$swal({
                    html: message,
                    showCancelButton: true,
                    confirmButtonText: 'Convert',
                    cancelButtonText: 'Cancel',
                    confirmButtonColor: '#E67E22',
                    reverseButtons: true,
                    customClass: 'swal-wider',
                })
                if (!result.value) {
                    return
                }
                await this.axios.post(
                    `/api/questions/linked-duplicate/${this.question.id}`,
                    {
                        unit_id: this.unit.id,
                    }
                )
                this.$emit('update')
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to duplicate linked question: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            }
        },

    },
    created () {
        if (this.question) {
            this.question_cache = _.cloneDeep(this.question)
        }
    }
}
</script>

<style lang="scss" scoped>
.question::v-deep .card-header {
    padding: 0;
}

.question-header-content {
    padding: 1.25rem;
}
</style>
